import React, { useContext } from 'react';

import { reloadCompany } from 'common/actions/company';
import { type PostType } from 'common/actions/queueItemQueries';
import Accordion from 'common/common/Accordion';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { getEncodedQueryParams, getQueryParams } from 'common/containers/QueueItemListContainer';
import connect from 'common/core/connect';
import CheckboxWithLabel from 'common/ui/CheckboxWithLabel';

import { PostTypes } from './consts';
import FiltersPopover from './FiltersPopover';
import useFilterControls from './useFilterControls';

import 'css/components/subdomain/admin/_AdminQueueFilters.scss';

import type { Dispatch, State } from 'redux-connect';

const Sources = [
  { name: 'gong', label: 'Gong' },
  {
    name: 'helpscout',
    label: 'Help Scout',
  },
  {
    name: 'intercom',
    label: 'Intercom',
  },
  {
    name: 'zendesk',
    label: 'Zendesk',
  },
  {
    name: 'api',
    label: 'API',
  },
] as const;

const getEnabledSources = (activeIntegrations: Record<string, boolean>) => {
  return Sources.filter((source) => activeIntegrations[source.name] || source.name === 'api');
};

type Props = {
  reloadCompany: () => void;
  queryState: Record<string, string | string[] | undefined> | null;
};

const ExternalSuggestionFilters = ({ reloadCompany, queryState }: Props) => {
  const company = useContext(CompanyContext);
  const { queryParams, updateFilters } = useFilterControls({
    getEncodedQueryParams,
    getQueryParams,
    preferencesKey: 'queueFilters',
    queryState,
    reloadCompany,
  });

  const enabledSources = queryParams.sources ?? [];
  const enabledPostTypes = queryParams.postTypes ?? [];

  const resetFilters = () =>
    updateFilters({ ...queryParams, sources: undefined, postTypes: undefined });

  const onToggleSource = (sourceName: string) => {
    const updatedFilteredSources = enabledSources.includes(sourceName)
      ? enabledSources.filter((b: string) => b !== sourceName)
      : enabledSources.concat(sourceName);

    updateFilters({
      ...queryParams,
      sources: updatedFilteredSources.length ? updatedFilteredSources : undefined,
    });
  };

  const onTogglePostType = (postType: PostType) => {
    const updatedPostTypes = enabledPostTypes.includes(postType)
      ? enabledPostTypes.filter((pt: string) => pt !== postType)
      : enabledPostTypes.concat(postType);

    updateFilters({
      ...queryParams,
      postTypes: updatedPostTypes.length ? updatedPostTypes : undefined,
    });
  };

  if (getEnabledSources(company.activeIntegrations).length === 0) {
    return null;
  }

  return (
    <FiltersPopover resetFilters={resetFilters}>
      <Accordion className="filterAccordion" title="Sources">
        <div className="accordionCheckboxes">
          {getEnabledSources(company.activeIntegrations).map((source) => (
            <CheckboxWithLabel
              key={source.name}
              onChange={() => onToggleSource(source.name)}
              checked={enabledSources.includes(source.name)}
              size="medium">
              {source.label}
            </CheckboxWithLabel>
          ))}
        </div>
      </Accordion>
      <Accordion className="filterAccordion" title="Types">
        <div className="accordionCheckboxes">
          {PostTypes.map((type) => (
            <CheckboxWithLabel
              checked={enabledPostTypes.includes(type.name)}
              onChange={() => onTogglePostType(type.name)}
              key={type.name}
              size="medium">
              {type.label}
            </CheckboxWithLabel>
          ))}
        </div>
      </Accordion>
    </FiltersPopover>
  );
};

export default connect(
  (state: State) => ({
    // Fetch the query as it currently exists, not from `location`. `location` waits until the route is ready,
    // but we're using the query string as state, so we need it to update immediately.
    queryState: state.routing.locationBeforeTransitions.query,
  }),
  (dispatch: Dispatch) => ({
    reloadCompany: () => dispatch(reloadCompany()),
  })
)(ExternalSuggestionFilters);
